import * as React from 'react';
import { Theme, alpha } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import Divider from '@mui/material/Divider';
import * as yup from 'yup';
import { useFormik, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useToastError } from '@hooks';
import { useUpdateSpotSuitableMutation, Spot, UpdateSpotSuitableInput } from '@graphql';

interface RenderSwitchProps {
  suitable: keyof UpdateSpotSuitableInput;
}
interface SpotSuitableProps {
  isActive: boolean;
  account: Spot;
}

export function SpotSuitable(props: Readonly<SpotSuitableProps>) {
  const { isActive, account } = props;
  const toastError = useToastError();
  const { t } = useTranslation('cta');

  const [updateSpotSuitable] = useUpdateSpotSuitableMutation();

  const initialValues = React.useMemo(() => {
    return Object.entries(account.suitable)
      .filter(([key, value]) => !['__typename', 'id', 'updatedAt'].includes(key))
      .reduce(
        (acc, [key, value]) => {
          acc[key as keyof UpdateSpotSuitableInput] = value;
          return acc;
        },
        {} as Record<keyof UpdateSpotSuitableInput, boolean | null>
      );
  }, [account.suitable]);

  const validationSchema = React.useMemo(() => {
    return yup.object(
      Object.keys(account.suitable)
        .filter((key) => !['__typename', 'id', 'updatedAt'].includes(key))
        .reduce(
          (acc, key) => {
            acc[key as keyof UpdateSpotSuitableInput] = yup.boolean().nullable().defined();
            return acc;
          },
          {} as Record<keyof UpdateSpotSuitableInput, yup.BooleanSchema<boolean | null>>
        )
    );
  }, [account.suitable]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      if (formik.dirty) {
        updateSpotSuitable({
          variables: { input: values },
          onCompleted: ({ updateSpotSuitable }) => {
            formik.resetForm({ values });
            toast(t('spotSuitable.toast.success', { ns: 'field' }), { type: 'success' });
          },
          onError: toastError,
        });
      }
    },
  });

  React.useEffect(() => {
    return () => {
      // trigger the mutation when the component is not anymore in view and the form is dirty
      if (isActive && formik.dirty) {
        formik.submitForm();
      }
    };
  }, [isActive, formik.dirty]);

  if (account?.__typename !== 'Spot') {
    return null;
  }

  return (
    <Stack width="100%" spacing={3}>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{
          p: 3,
          borderRadius: (theme: Theme) => theme.borderRadius,
          backgroundColor: alpha('#fff', 0.1),
        }}
      >
        <Stack>
          <Typography fontWeight="bold">{t('spotSuitable.label', { ns: 'field' })}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t('spotSuitable.caption', { ns: 'field' })}
          </Typography>
        </Stack>
      </Stack>
      <Stack
        spacing={2}
        divider={<Divider sx={{ backgroundColor: (theme: Theme) => theme.palette.grey[800] }} />}
      >
        {Object.keys(account.suitable)
          .filter((key) => !['__typename', 'id', 'updatedAt'].includes(key))
          .map((suitable) => (
            <Stack
              key={suitable}
              spacing={2}
              direction="row"
              width="100%"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="caption" color="text.primary">
                {t(`suitable.${suitable}`, { ns: 'spot' })}
              </Typography>
              <RenderSwitch suitable={suitable as RenderSwitchProps['suitable']} formik={formik} />
            </Stack>
          ))}
      </Stack>
    </Stack>
  );
}

function RenderSwitch(props: RenderSwitchProps & { formik: FormikProps<any> }) {
  const { suitable, formik } = props;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    formik.setFieldValue(name, checked);
  };

  return (
    <Switch name={suitable} checked={!!formik.values[suitable]} onChange={handleChange} color={'success'} />
  );
}
